import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import onClickOutside from 'react-onclickoutside';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Button, Glyphicon, OverlayTrigger, Tooltip } from '@bingads-webui-react/primitive';
import { PredicateList } from '@bingads-webui/predicate-list-react';
import {
  withPopover,
  MaximumSavedFiltersLimit,
  FilterBarIngoreClickOutsideClassName,
} from '@bingads-webui/filter-commons';

import { getFilterBarStyles } from './filter-bar-style';
import { SaveFilterView } from './save-filter-view';

const SaveFilterPopover = withPopover(SaveFilterView);

class FilterBarComponentUnthemed extends React.PureComponent {
  state = {
    showSaveFilterPopover: false,
  }

  onSaveFilter = (filterName) => {
    this.onClose();
    this.props.onSaveFilter(filterName);
  }

  onClose = () => {
    this.setState({
      showSaveFilterPopover: false,
    });
  }

  showSaveFilterPopover = () => {
    this.setState({
      showSaveFilterPopover: true,
    });
  }

  handleClickOutside = () => {
    this.props.hideFilterBar();
  };

  // TODO Will switch to use new i18ns and update the correct icon for save
  render() {
    const customDisabledSaveFilterReason =
    _.isFunction(this.props.disabledSaveFilterButtonWithReason) ?
      this.props.disabledSaveFilterButtonWithReason(this.props) :
      this.props.disabledSaveFilterButtonWithReason;
    const customDisabledSaveFilterButton = customDisabledSaveFilterReason !== '';
    const disabledSaveFilterButton = (this.props.savedFilters
      && this.props.savedFilters.length >= MaximumSavedFiltersLimit)
      || customDisabledSaveFilterButton;

    const saveFilterButton = (
      <Button
        disabled={disabledSaveFilterButton}
        onClick={this.showSaveFilterPopover}
        className="filter-bar-button"
        aria-label={this.props.i18n.getString(_TL_('Add to saved filters'))}
        style={customDisabledSaveFilterButton ? { pointerEvents: 'none' } : {}}
      >
        <Glyphicon glyph="ba-Copy" />
        {this.props.i18n.getString(_TL_('Save'))}
      </Button>);
    const saveFilterPopover = (
      <SaveFilterPopover
        show={this.state.showSaveFilterPopover}
        onClose={this.onClose}
        id="save-filter-popover"
        onCancelSaveFilter={this.onClose}
        onSaveFilter={this.onSaveFilter}
        i18n={this.props.i18n}
        legacyI18n={this.props.legacyI18n}
        classes={{ calloutTargetContainer: 'save-filter-button' }}
        popoverClassName={
        classNames(this.props.classes.SaveFilterPopover, FilterBarIngoreClickOutsideClassName)
      }
      >
        {customDisabledSaveFilterButton ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip
                id="disabled-save-filter-reason"
              >
                {customDisabledSaveFilterReason}
              </Tooltip>
            }
          >
            <div style={{ display: 'inline-block', cursor: 'not-allowed' }}>
              {saveFilterButton}
            </div>
          </OverlayTrigger>) : saveFilterButton}
      </SaveFilterPopover>);
    const hideSaveFilterButton = _.isFunction(this.props.hideSaveFilterButton) ?
      this.props.hideSaveFilterButton(this.props) : this.props.hideSaveFilterButton;

    const viewStateFilterPillsEnabled = _.result(this.props.permissions.dynamic, 'ToolbarEditStateFiltersTextPrefix', false);

    return (
      <div className={`ba-filter-bar ${this.props.classes.FilterBar}`}>
        {viewStateFilterPillsEnabled ? (
          <div className="filter-bar-filter-text">
            {this.props.i18n.getString(_TL_('Filters:'))}
          </div>) : null }
        <PredicateList
          {...this.props}
          popoverContainer={this}
        />
        <div className="filter-bar-button-group">
          {!hideSaveFilterButton && saveFilterPopover}
          <Button
            onClick={this.props.onReset}
            className="filter-bar-button"
            aria-label={this.props.i18n.getString(_TL_('Reset filter to default state'))}
          >
            <Glyphicon glyph="ba-Refresh" />
            {this.props.i18n.getString(_TL_('Reset'))}
          </Button>
          <div className="filter-bar-divider" />
          <Button
            onClick={this.props.hideFilterBar}
            className="filter-bar-cancel-button filter-bar-button"
            aria-label={this.props.i18n.getString(_TL_('Close filter panel'))}
          >
            <Glyphicon glyph="ba-Cancel" />
          </Button>
        </div>
      </div>
    );
  }
}

FilterBarComponentUnthemed.propTypes = {
  hideFilterBar: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    FilterBar: PropTypes.string.isRequired,
    SaveFilterPopover: PropTypes.string.isRequired,
  }).isRequired,
  onSaveFilter: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  legacyI18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  savedFilters: PropTypes.arrayOf(PropTypes.shape()),
  hideSaveFilterButton: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  disabledSaveFilterButtonWithReason: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  getPredicateIconClassName: PropTypes.func,
  predicateValueNameGetter: PropTypes.func,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({}),
  }),
};

FilterBarComponentUnthemed.defaultProps = {
  savedFilters: [],
  hideSaveFilterButton: false,
  disabledSaveFilterButtonWithReason: '',
  getPredicateIconClassName: undefined,
  predicateValueNameGetter: undefined,
  permissions: { dynamic: {} },
};

export const FilterBar =
withDefaultStyles(onClickOutside(FilterBarComponentUnthemed), getFilterBarStyles);
